import { useState, useEffect } from 'react';
import { formatDateTimeDisplay } from '../utils/dateUtils';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Textarea,
  Avatar,
  Divider,
  useToast,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Input,
  Link,
  Badge,
  Select,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, DeleteIcon, EditIcon, AttachmentIcon, CloseIcon } from '@chakra-ui/icons';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const CommentSection = ({ ticketId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [sortPreference, setSortPreference] = useState('oldest');
  const [newComment, setNewComment] = useState('');
  const [newReply, setNewReply] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [expandedThreads, setExpandedThreads] = useState({});
  const [editingComment, setEditingComment] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [newCommentFiles, setNewCommentFiles] = useState([]);
  const [newReplyFiles, setNewReplyFiles] = useState({});
  const toast = useToast();

  const sortComments = (comments, sortOrder) => {
    return [...comments].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      
      if (sortOrder === 'oldest') {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  };

  const sortRepliesChronologically = (replies) => {
    return [...replies].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateA - dateB; // Always oldest first for replies
    });
  };

  const fetchComments = async () => {
    try {
      const response = await api.get(`/comments?ticketId=${ticketId}`);
      const comments = response.data.data;
      
      // Fetch replies for each comment
      const commentsWithReplies = await Promise.all(
        comments.map(async (comment) => {
          const repliesResponse = await api.get(`/comments/thread/${comment._id}`);
          const sortedReplies = sortRepliesChronologically(repliesResponse.data.data);
          return {
            ...comment,
            replies: sortedReplies
          };
        })
      );
      
      const sortedComments = sortComments(commentsWithReplies, sortPreference);
      setComments(sortedComments);
    } catch (error) {
      toast({
        title: 'Error fetching comments',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const applySortingToComments = () => {
    setComments(prevComments => 
      sortComments(prevComments, sortPreference).map(comment => ({
        ...comment,
        replies: comment.replies ? sortRepliesChronologically(comment.replies) : []
      }))
    );
  };

  useEffect(() => {
    if (comments.length > 0) {
      applySortingToComments();
    }
  }, [sortPreference]);

  useEffect(() => {
    fetchComments();
  }, [ticketId]);

  const handleFileChange = (event, parentCommentId = null) => {
    const files = Array.from(event.target.files);
    if (parentCommentId) {
      setNewReplyFiles(prev => ({
        ...prev,
        [parentCommentId]: [...(prev[parentCommentId] || []), ...files]
      }));
    } else {
      setNewCommentFiles(prev => [...prev, ...files]);
    }
  };

  const removeFile = (index, parentCommentId = null) => {
    if (parentCommentId) {
      setNewReplyFiles(prev => ({
        ...prev,
        [parentCommentId]: prev[parentCommentId].filter((_, i) => i !== index)
      }));
    } else {
      setNewCommentFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmitComment = async (parentCommentId = null) => {
    const content = parentCommentId ? newReply[parentCommentId] : newComment;
    if (!content?.trim() && (!parentCommentId ? newCommentFiles.length === 0 : newReplyFiles[parentCommentId]?.length === 0)) return;

    try {
      setIsLoading(true);
      const formData = new FormData();
      if(content) {
        formData.append('content', content);
      }
      formData.append('ticketId', ticketId);
      if (parentCommentId) {
        formData.append('parentCommentId', parentCommentId);
      }

      const files = parentCommentId ? newReplyFiles[parentCommentId] : newCommentFiles;
      files?.forEach(file => {
        formData.append('files', file);
      });

      const response = await api.post('/comments', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setComments(prevComments => {
        if (parentCommentId) {
          // Add reply to the parent comment's thread and sort replies chronologically
          return prevComments.map(comment => {
            if (comment._id === parentCommentId) {
              const updatedReplies = [...(comment.replies || []), response.data.data];
              return {
                ...comment,
                replies: sortRepliesChronologically(updatedReplies),
              };
            }
            return comment;
          });
        } else {
          // Add new top-level comment and sort all comments
          const updatedComments = [...prevComments, response.data.data];
          return sortComments(updatedComments, sortPreference);
        }
      });

      if (parentCommentId) {
        setNewReply(prev => ({ ...prev, [parentCommentId]: '' }));
        setNewReplyFiles(prev => ({ ...prev, [parentCommentId]: [] }));
      } else {
        setNewComment('');
        setNewCommentFiles([]);
      }
      
      toast({
        title: 'Comment added',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error adding comment',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateComment = async (commentId) => {
    if (!editContent.trim()) return;

    try {
      const response = await api.put(`/comments/${commentId}`, {
        content: editContent,
      });

      setComments(prevComments => 
        prevComments.map(comment => {
          // Check if this is the comment being updated
          if (comment._id === commentId) {
            return response.data.data;
          }
          // Check if the comment has replies and one of them is being updated
          if (comment.replies) {
            return {
              ...comment,
              replies: comment.replies.map(reply =>
                reply._id === commentId ? response.data.data : reply
              ),
            };
          }
          return comment;
        })
      );

      setEditingComment(null);
      setEditContent('');
      toast({
        title: 'Comment updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating comment',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/comments/${commentId}`);
      
      setComments(prevComments => 
        prevComments.map(comment => {
          // If this is the comment being deleted, remove it
          if (comment._id === commentId) {
            return null;
          }
          // If this comment has replies, filter out the deleted reply
          if (comment.replies) {
            return {
              ...comment,
              replies: comment.replies.filter(reply => reply._id !== commentId)
            };
          }
          return comment;
        }).filter(Boolean) // Remove null entries (deleted comments)
      );

      toast({
        title: 'Comment deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error deleting comment',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const toggleThread = (commentId) => {
    setExpandedThreads(prev => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const renderAttachments = (comment) => {
    if (!comment.attachments || comment.attachments.length === 0) return null;

    return (
      <VStack align="stretch" spacing={2} mt={2}>
        {comment.attachments.map((attachment) => (
          <HStack key={attachment._id} p={1}>
            <AttachmentIcon color="gray.500" />
            <Link
              href={`${process.env.REACT_APP_API_URL}/comments/${comment._id}/attachments/${attachment._id}`}
              target="_blank"
              rel="noopener noreferrer"
              color="brand.500"
            >
              {attachment.originalname}
            </Link>
            <Text color="gray.500" fontSize="sm">
              {(attachment.size / 1024).toFixed(2)} KB
            </Text>
          </HStack>
        ))}
      </VStack>
    );
  };

  const renderFileInput = (parentCommentId = null) => {
    const files = parentCommentId ? newReplyFiles[parentCommentId] : newCommentFiles;
    
    return (
      <Box mt={2} mb={2}>
        <Input
          type="file"
          multiple
          onChange={(e) => handleFileChange(e, parentCommentId)}
          display="none"
          id={`file-input-${parentCommentId || 'new'}`}
        />
        <Button
          as="label"
          htmlFor={`file-input-${parentCommentId || 'new'}`}
          size="sm"
          variant="outline"
          leftIcon={<AttachmentIcon />}
          mr={2}
        >
          Attach Files
        </Button>
        {files && files.length > 0 && (
          <VStack align="stretch" mt={2}>
            {files.map((file, index) => (
              <HStack key={index} spacing={2}>
                <Text fontSize="sm">{file.name}</Text>
                <IconButton
                  size="xs"
                  icon={<CloseIcon />}
                  onClick={() => removeFile(index, parentCommentId)}
                />
              </HStack>
            ))}
          </VStack>
        )}
      </Box>
    );
  };

  const renderComment = (comment, isReply = false) => {
    const isAuthor = comment.author._id === user._id;
    const isAdmin = user.role === 'admin';
    const canModify = isAuthor || isAdmin;

    return (
      <Box
        key={comment._id}
        bg="white"
        p={3}
        borderRadius="md"
        boxShadow="sm"
        ml={isReply ? 8 : 0}
        mb={2}
        borderLeft="2px solid"
        borderLeftColor="brand.200"
      >
        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between" align="center">
            <HStack spacing={2}>
              <Avatar
                size="xs"
                name={comment.author.name}
                src={comment.author.avatar}
              />
              <Box>
                <HStack spacing={2}>
                  <Text fontSize="sm" fontWeight="bold">{comment.author.name}</Text>
                  {!isReply && comment.replies && comment.replies.length > 0 && (
                    <Badge colorScheme="brand" fontSize="xs">
                      {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="xs" color="gray.500">
                  {formatDateTimeDisplay(comment.createdAt)}
                </Text>
              </Box>
            </HStack>
            {canModify && (
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<ChevronDownIcon />}
                  variant="ghost"
                  size="xs"
                />
                <MenuList>
                  <MenuItem
                    icon={<EditIcon />}
                    onClick={() => {
                      setEditingComment(comment._id);
                      setEditContent(comment.content);
                    }}
                  >
                    Edit
                  </MenuItem>
                  <MenuItem
                    icon={<DeleteIcon />}
                    color="red.500"
                    onClick={() => handleDeleteComment(comment._id)}
                  >
                    Delete
                  </MenuItem>
                </MenuList>
              </Menu>
            )}
          </HStack>

          {editingComment === comment._id ? (
            <Box>
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                size="sm"
                mb={2}
              />
              <HStack>
                <Button
                  size="sm"
                  colorScheme="brand"
                  onClick={() => handleUpdateComment(comment._id)}
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingComment(null);
                    setEditContent('');
                  }}
                >
                  Cancel
                </Button>
              </HStack>
            </Box>
          ) : (
            <Box p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="sm" whiteSpace="pre-wrap">{comment.content}</Text>
              {renderAttachments(comment)}
            </Box>
          )}

          {!isReply && (
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => toggleThread(comment._id)}
              >
                {expandedThreads[comment._id] ? (
                  <ChevronUpIcon mr={1} />
                ) : (
                  <ChevronDownIcon mr={1} />
                )}
                {expandedThreads[comment._id] ? 'Hide Replies' : 'Show Replies'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setExpandedThreads(prev => ({
                    ...prev,
                    [comment._id]: true,
                  }));
                  setNewReply(prev => ({ ...prev, [comment._id]: '' }));
                }}
              >
                Reply
              </Button>
            </HStack>
          )}

        </VStack>
      </Box>
    );
  };

  return (
    <Box>
      <HStack justify="space-between" align="center" mb={4}>
        <Text fontSize="xl" fontWeight="bold">
          Comments
        </Text>
        <HStack spacing={2}>
          <Text fontSize="sm" color="gray.600">Sort by:</Text>
          <Select
            size="sm"
            width="120px"
            value={sortPreference}
            onChange={(e) => setSortPreference(e.target.value)}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </Select>
        </HStack>
      </HStack>
      <Divider />

      <VStack align="stretch" spacing={4} mt={4}>
        {comments.map(comment => (
          <Box key={comment._id}>
            {renderComment(comment)}
            {expandedThreads[comment._id] && comment.replies && (
              <VStack align="stretch" mt={2} spacing={2}>
                {comment.replies.map(reply => renderComment(reply, true))}
                <Box p={3} borderRadius="md">
                  <Textarea
                    placeholder="Write a reply..."
                    value={newReply[comment._id] || ''}
                    onChange={(e) => setNewReply(prev => ({
                      ...prev,
                      [comment._id]: e.target.value
                    }))}
                    size="sm"
                    mb={2}
                  />
                  {renderFileInput(comment._id)}
                  <Button
                    size="sm"
                    colorScheme="brand"
                    isLoading={isLoading}
                    onClick={() => handleSubmitComment(comment._id)}
                    mt={2}
                  >
                    Post Reply
                  </Button>
                </Box>
              </VStack>
            )}
          </Box>
        ))}
      </VStack>
      
      <Box mt={6} mb={4}>
        <Divider mb={4} />
        <Textarea
          placeholder="Write a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          mb={2}
        />
        {renderFileInput()}
        <Button
          colorScheme="brand"
          isLoading={isLoading}
          onClick={() => handleSubmitComment()}
          mt={2}
        >
          Post Comment
        </Button>
      </Box>
    </Box>
  );
};

export default CommentSection;
